import React, { useEffect, useState } from 'react';
import { Box, Grid, Card, CardContent, TextField, MenuItem, Button, Stack, Typography, Chip, Stepper, Step, StepLabel, Paper, Tooltip, InputAdornment, Table, TableBody, TableCell, TableHead, TableRow, Divider, IconButton, Alert } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { jsPDF } from 'jspdf';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { useForm, Controller } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import applicationsService from '../../services/applicationsService';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import { STATUS_OPTIONS } from '../../config/recruitmentStatuses';
import { SELECTABLE_SOURCES, normalizeSource, sourceLabel } from '../../config/recruitmentSources';
import CandidateAttributeFields from '../recruitment/CandidateAttributeFields';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

// Një burim i vetëm: e njëjta listë që përdorin lista, gypi dhe intervistat.
const STATUS = STATUS_OPTIONS;

// Burimet vijnë nga fjalori i përbashkët: lista e shkruar këtu dhe raporti që
// grupon sipas burimit do të rrëshqisnin veç e veç.
// Burimet që i vendos sistemi vetë (s'zgjidhen me dorë, por duhet të shfaqen):
// `public` = aplikim nga faqja publike e pozitës. Pa këtë, fusha dilte bosh.


export default function ApplicationForm() {
  const history = useHistory();
  const { id } = useParams();
  const { control, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      position: '',
      listName: '',
      source: 'linkedin',
      status: 'applied',
      appliedAt: new Date().toISOString().slice(0, 10),
      rejectReason: '',
      workerId: '',
      stepComments: {},
      // Forma e krahasueshme e të dhënave — përdoret nga kërkimi historik.
      attributes: {},
      reuseConsent: false,
      // Te dhenat e reja
      city: '',
      address: '',
      dateOfBirth: '',
      education: '',
      experience: '',
      skills: '',
      linkedinUrl: '',
      portfolioUrl: '',
      referredBy: '',
      notes: '',
    },
  });

  const [prev, setPrev] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [stepNote, setStepNote] = useState('');
  const [pendingHistory, setPendingHistory] = useState([]);
  const [brandColor, setBrandColor] = useState('#3E8FA3');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      // settings (brand color & company info)
      try {
        const s = await apiService().get('settings');
        if (s?.success && s.data?.length) {
          const settings = s.data[0];
          if (settings?.BrandColor) setBrandColor(String(settings.BrandColor));
          setCompanyInfo(settings);
        }
      } catch (_) {}

      // load workers once for optional link on hire
      try {
        const w = await apiService().get('workers');
        if (w?.success && mounted) setWorkers(w.data || []);
      } catch (_) {}

      if (!id) return;
      const data = await applicationsService().get(id);
      if (mounted && data) {
        reset({
          ...data,
          appliedAt: (data.appliedAt || '').slice(0, 10),
          dateOfBirth: (data.dateOfBirth || '').slice(0, 10),
        });
        setPrev(data);
        if (data.files) setUploadedFiles(data.files);
      }
    }
    load();
    return () => (mounted = false);
  }, [id, reset]);

  const onSubmit = async (values) => {
    const now = new Date().toISOString();
    const payload = { 
      ...values, 
      appliedAt: new Date(values.appliedAt).toISOString(), 
      dateOfBirth: values.dateOfBirth ? new Date(values.dateOfBirth).toISOString() : null,
      id 
    };
    const events = [];
    if (prev) {
      if (prev.status !== values.status) {
        events.push({ date: now, type: 'status', from: prev.status, to: values.status, comment: values.status === 'rejected' ? values.rejectReason : '' });
      }
    }
    if (values.status === 'rejected' && !values.rejectReason) {
      alert('Shkruaj arsyen e refuzimit.');
      return;
    }
    const stepComments = values.stepComments || {};
    // Serveri e rishkruan tërë kolonën `history`; pa ngjarjet e mëparshme, çdo
    // ndryshim statusi fshinte historikun e deriatëhershëm të kandidatit.
    if (events.length || pendingHistory.length) payload.history = [...(prev?.history || []), ...events, ...pendingHistory];
    payload.stepComments = stepComments;
    payload.files = uploadedFiles;
    await applicationsService().save(payload);
    history.push('/applications');
  };

  const status = watch('status');

  const setStatus = (s) => reset({ ...watch(), status: s });

  // Keep dependent fields clean when status changes (avoid stale values).
  useEffect(() => {
    if (status !== 'rejected') setValue('rejectReason', '');
    if (status !== 'hired') setValue('workerId', '');
  }, [status, setValue]);

  const STEPS = STATUS.map((s) => s.value);

  // Small helper to attach an info tooltip to the end of inputs
  const tip = (text) => ({
    InputProps: {
      endAdornment: (
        <InputAdornment position="end">
          <Tooltip title={text} placement="top" arrow>
            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </InputAdornment>
      ),
    },
  });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <PageHeader title={id ? 'Ndrysho aplikimin' : 'Shto aplikim'} />
      <Card 
        elevation={0} 
        sx={{ 
          mb: 2, 
          border: '1px solid', 
          borderColor: 'divider',
          borderRadius: 2
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }} 
            spacing={{ xs: 1.5, sm: 2 }}
            sx={{ mb: { xs: 2, md: 2.5 } }}
          >
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              {id ? 'Ndrysho aplikim' : 'Shto aplikim të ri'}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button 
                variant="outlined" 
                startIcon={<PictureAsPdfIcon />}
                onClick={() => {
                  const v = watch();
                  const hexToRgb = (hex) => {
                    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '#3E8FA3');
                    return m ? [parseInt(m[1],16), parseInt(m[2],16), parseInt(m[3],16)] : [79,70,229];
                  };
                  const [r,g,b] = hexToRgb(brandColor);

                  const doc = new jsPDF('p','mm', 'a4'); 
                  const w = doc.internal.pageSize.getWidth();
                  const h = doc.internal.pageSize.getHeight();
                  let y = 0;

                  // Professional Header with Brand Color
                  doc.setFillColor(r,g,b); 
                  doc.rect(0,0,w,35,'F');
                  doc.setTextColor(255,255,255); 
                  doc.setFontSize(20); 
                  doc.setFont('helvetica', 'bold');
                  doc.text('PROFILI I KANDIDATIT', w/2, 15, { align: 'center' });
                  doc.setFontSize(12);
                  doc.setFont('helvetica', 'normal');
                  const companyName = companyInfo?.CompanyName || companyInfo?.name || 'HR Portal';
                  doc.text(companyName, w/2, 22, { align: 'center' });
                  doc.setFontSize(9);
                  doc.text(`Raporti i aplikimit - ${new Date().toLocaleDateString('sq-AL')}`, w/2, 28, { align: 'center' });
                  
                  doc.setTextColor(0,0,0);
                  y = 42;

                  // Candidate Name - Prominent
                  doc.setFontSize(16);
                  doc.setFont('helvetica', 'bold');
                  doc.text(v.fullName || 'Emri nuk është dhënë', 15, y);
                  y += 8;

                  // Position Applied For
                  doc.setFontSize(11);
                  doc.setFont('helvetica', 'normal');
                  doc.setTextColor(100,100,100);
                  doc.text(`Pozita: ${v.position || '—'}`, 15, y);
                  y += 10;

                  // Section 1: Personal Information
                  doc.setTextColor(0,0,0);
                  doc.setFontSize(13);
                  doc.setFont('helvetica', 'bold');
                  doc.text('TË DHËNAT PERSONALE', 15, y);
                  y += 2;
                  doc.setFillColor(r,g,b);
                  doc.rect(15, y, 40, 0.5, 'F');
                  y += 6;

                  doc.setFontSize(10);
                  doc.setFont('helvetica', 'normal');
                  const personalData = [
                    ['Email:', v.email || '—'],
                    ['Telefoni:', v.phone || '—'],
                    ['Qyteti:', v.city || '—'],
                    ['Adresa:', v.address || '—'],
                    ['Data e lindjes:', v.dateOfBirth ? new Date(v.dateOfBirth).toLocaleDateString('sq-AL') : '—'],
                  ];
                  personalData.forEach(([label, value]) => {
                    doc.setFont('helvetica', 'bold');
                    doc.text(label, 15, y);
                    doc.setFont('helvetica', 'normal');
                    doc.text(value, 50, y);
                    y += 6;
                  });
                  y += 3;

                  // Section 2: Application Details
                  doc.setFontSize(13);
                  doc.setFont('helvetica', 'bold');
                  doc.text('DETAJET E APLIKIMIT', 15, y);
                  y += 2;
                  doc.setFillColor(r,g,b);
                  doc.rect(15, y, 45, 0.5, 'F');
                  y += 6;

                  doc.setFontSize(10);
                  doc.setFont('helvetica', 'normal');
                  const applicationData = [
                    ['Pozita:', v.position || '—'],
                    ['Lista/Vacancy:', v.listName || '—'],
                    ['Burimi:', sourceLabel(v.source)],
                    ['Statusi:', STATUS.find(s => s.value === v.status)?.label || v.status],
                    ['Data e aplikimit:', v.appliedAt ? new Date(v.appliedAt).toLocaleDateString('sq-AL') : '—'],
                    ['LinkedIn:', v.linkedinUrl || '—'],
                    ['Portfolio:', v.portfolioUrl || '—'],
                    ['Rekomanduar nga:', v.referredBy || '—'],
                    ['Pëlqimi për ruajtjen e të dhënave:', v.consentGiven ? 'Po' + (v.consentAt ? ` (${new Date(v.consentAt).toLocaleDateString('sq-AL')})` : '') : 'Jo'],
                  ];
                  applicationData.forEach(([label, value]) => {
                    doc.setFont('helvetica', 'bold');
                    doc.text(label, 15, y);
                    doc.setFont('helvetica', 'normal');
                    const text = doc.splitTextToSize(String(value), w - 55);
                    doc.text(text, 50, y);
                    y += text.length > 1 ? text.length * 5 : 6;
                  });
                  y += 3;

                  // Section 3: Education & Experience
                  if (v.education || v.experience || v.skills) {
                    if (y > h - 40) { doc.addPage(); y = 20; }
                    doc.setFontSize(13);
                    doc.setFont('helvetica', 'bold');
                    doc.text('ARSIMI & PËRVOJA', 15, y);
                    y += 2;
                    doc.setFillColor(r,g,b);
                    doc.rect(15, y, 40, 0.5, 'F');
                    y += 6;

                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'normal');

                    if (v.education) {
                      doc.setFont('helvetica', 'bold');
                      doc.text('Arsimi:', 15, y);
                      y += 5;
                      doc.setFont('helvetica', 'normal');
                      const eduLines = doc.splitTextToSize(v.education, w - 30);
                      doc.text(eduLines, 15, y);
                      y += eduLines.length * 5 + 3;
                    }

                    if (v.experience) {
                      if (y > h - 30) { doc.addPage(); y = 20; }
                      doc.setFont('helvetica', 'bold');
                      doc.text('Përvoja:', 15, y);
                      y += 5;
                      doc.setFont('helvetica', 'normal');
                      const expLines = doc.splitTextToSize(v.experience, w - 30);
                      doc.text(expLines, 15, y);
                      y += expLines.length * 5 + 3;
                    }

                    if (v.skills) {
                      if (y > h - 30) { doc.addPage(); y = 20; }
                      doc.setFont('helvetica', 'bold');
                      doc.text('Aftësitë:', 15, y);
                      y += 5;
                      doc.setFont('helvetica', 'normal');
                      const skillLines = doc.splitTextToSize(v.skills, w - 30);
                      doc.text(skillLines, 15, y);
                      y += skillLines.length * 5 + 3;
                    }
                    y += 3;
                  }

                  // Section 4: Notes
                  if (v.notes) {
                    if (y > h - 30) { doc.addPage(); y = 20; }
                    doc.setFontSize(13);
                    doc.setFont('helvetica', 'bold');
                    doc.text('SHËNIME', 15, y);
                    y += 2;
                    doc.setFillColor(r,g,b);
                    doc.rect(15, y, 25, 0.5, 'F');
                    y += 6;
                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'normal');
                    const notesLines = doc.splitTextToSize(v.notes, w - 30);
                    doc.text(notesLines, 15, y);
                    y += notesLines.length * 5 + 5;
                  }

                  // Section 5: Process History
                  const hist = [...(prev?.history||[]), ...pendingHistory];
                  if (hist.length) {
                    if (y > h - 40) { doc.addPage(); y = 20; }
                    doc.setFontSize(13);
                    doc.setFont('helvetica', 'bold');
                    doc.text('HISTORIKU I PROCESIT', 15, y);
                    y += 2;
                    doc.setFillColor(r,g,b);
                    doc.rect(15, y, 45, 0.5, 'F');
                    y += 6;

                    doc.setFontSize(9);
                    doc.setFont('helvetica', 'bold');
                    doc.setFillColor(240,242,247);
                    doc.rect(15, y, w - 30, 8, 'F');
                    doc.text('DATA & ORA', 17, y + 5);
                    doc.text('NDRYSHIMI', 65, y + 5);
                    doc.text('DETAJET', 120, y + 5);
                    y += 10;

                    doc.setFont('helvetica', 'normal');
                    hist.forEach((h, idx) => {
                      if (y > h - 20) { doc.addPage(); y = 20; }
                      const dateStr = new Date(h.date).toLocaleString('sq-AL');
                      const type = h.type === 'status' ? `${h.from || ''} → ${h.to || ''}` : 
                                   (h.type === 'step-comment' ? `Koment (${h.step})` : 'Koment');
                      const desc = h.comment || '—';

                      // Alternate row colors
                      if (idx % 2 === 0) {
                        doc.setFillColor(250,250,250);
                        doc.rect(15, y - 4, w - 30, 8, 'F');
                      }

                      doc.text(dateStr, 17, y);
                      doc.text(doc.splitTextToSize(type, 50), 65, y);
                      const descLines = doc.splitTextToSize(desc, w - 125);
                      doc.text(descLines, 120, y);
                      y += Math.max(8, descLines.length * 5);
                    });
                  }

                  // Reject Reason if applicable
                  if (v.status === 'rejected' && v.rejectReason) {
                    if (y > h - 30) { doc.addPage(); y = 20; }
                    y += 5;
                    doc.setFontSize(11);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(220, 38, 38);
                    doc.text('ARSYEJA E REFUZIMIT:', 15, y);
                    y += 6;
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(0,0,0);
                    const rejectLines = doc.splitTextToSize(v.rejectReason, w - 30);
                    doc.text(rejectLines, 15, y);
                  }

                  // Uploaded Files List
                  if (uploadedFiles.length > 0) {
                    if (y > h - 30) { doc.addPage(); y = 20; }
                    y += 5;
                    doc.setTextColor(0,0,0);
                    doc.setFontSize(11);
                    doc.setFont('helvetica', 'bold');
                    doc.text('DOKUMENTA TË NGARKUAR:', 15, y);
                    y += 6;
                    doc.setFontSize(9);
                    doc.setFont('helvetica', 'normal');
                    uploadedFiles.forEach((file, idx) => {
                      doc.text(`${idx + 1}. ${file.name}`, 17, y);
                      y += 5;
                    });
                  }

                  // Footer
                  const footerY = h - 15;
                  doc.setFontSize(8);
                  doc.setTextColor(150,150,150);
                  doc.text(`Gjeneruar nga ${companyName} - ${new Date().toLocaleString('sq-AL')}`, w/2, footerY, { align: 'center' });
                  doc.text('Ky dokument është konfidencial', w/2, footerY + 4, { align: 'center' });

                  doc.save(`aplikimi_${(v.fullName||'kandidat').replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.pdf`);
                }}
                sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
              >
                Eksporto PDF
              </Button>
              <Button 
                variant="text" 
                onClick={() => history.goBack()}
                size="small"
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  display: { xs: 'none', sm: 'inline-flex' }
                }}
              >
                Anulo
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
                size="small"
                sx={{ 
                  bgcolor: brandColor, 
                  '&:hover': { bgcolor: brandColor },
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  flex: { xs: 1, sm: 0 }
                }}
              >
                {isSubmitting ? 'Duke ruajtur...' : 'Ruaj aplikimin'}
              </Button>
            </Stack>
          </Stack>

          <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: brandColor, borderRadius: 1 }} />
                  Të dhënat personale
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller name="fullName" control={control} rules={{ required: 'Emri dhe mbiemri është i detyrueshëm' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Emri dhe mbiemri"
                          required
                          fullWidth
                          size="small"
                          error={Boolean(errors.fullName)}
                          helperText={errors.fullName?.message}
                          {...tip('Shkruaj emrin e plotë të kandidatit si në CV.')}
                        />
                      )} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller name="phone" control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Telefoni" fullWidth size="small" {...tip('Numër telefoni për kontakt.')} />
                      )} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller name="email" control={control} rules={{
                      pattern: { value: /\S+@\S+\.\S+/, message: 'Email nuk është valid' },
                    }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Email"
                          type="email"
                          fullWidth
                          size="small"
                          error={Boolean(errors.email)}
                          helperText={errors.email?.message}
                          {...tip('Email kontakti për komunikim/intervista.')}
                        />
                      )} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller name="city" control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Qyteti" fullWidth size="small" {...tip('Qyteti ku jeton kandidati.')} />
                      )} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller name="dateOfBirth" control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Data e lindjes"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          {...tip('Data e lindjes e kandidatit.')}
                        />
                      )} />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller name="address" control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Adresa" fullWidth size="small" {...tip('Adresa e plotë e kandidatit.')} />
                      )} />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller name="referredBy" control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Rekomanduar nga" placeholder="Emri i personit që e rekomandoi" fullWidth size="small" {...tip('Nëse dikush brenda kompanisë e rekomandoi kandidatin, shkruani emrin e tij/saj.')} />
                      )} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: brandColor, borderRadius: 1 }} />
                  Pozita & aplikimi
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller name="position" control={control} rules={{ required: 'Pozita është e detyrueshme' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Pozita"
                          required
                          fullWidth
                          size="small"
                          error={Boolean(errors.position)}
                          helperText={errors.position?.message}
                          {...tip('Pozita për të cilën po aplikon kandidati.')}
                        />
                      )} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller name="listName" control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Lista / Vacancy" placeholder="p.sh. Backend 2025" fullWidth size="small" {...tip('Identifikues i shpalljes / grupit të aplikimeve.')} />
                      )} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller name="appliedAt" control={control} rules={{ required: 'Data e aplikimit është e detyrueshme' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Data e aplikimit"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          error={Boolean(errors.appliedAt)}
                          helperText={errors.appliedAt?.message}
                          {...tip('Data kur është marrë aplikimi.') }
                        />
                      )} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller name="source" control={control}
                      render={({ field }) => (
                        <TextField select {...field} label="Burimi" fullWidth size="small" {...tip('Nga erdhi aplikimi: LinkedIn, Website, Referim, etj.') }>
                          {SELECTABLE_SOURCES.map((s) => (
                            <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>
                          ))}
                          {/* «Faqja publike» e shkruan sistemi; shfaqet vetëm te
                              aplikimet që erdhën andej, dhe nuk zgjidhet me dorë. */}
                          {normalizeSource(field.value) === 'public' && (
                            <MenuItem value="public" disabled>Faqja publike</MenuItem>
                          )}
                        </TextField>
                      )} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller name="linkedinUrl" control={control}
                      render={({ field }) => (
                        <TextField {...field} label="LinkedIn URL" placeholder="https://linkedin.com/in/..." fullWidth size="small" {...tip('Linku i profilit LinkedIn.')} />
                      )} />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller name="portfolioUrl" control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Portfolio / Website" placeholder="https://..." fullWidth size="small" {...tip('Linku i portfolio ose website personal.')} />
                      )} />
                  </Grid>
                </Grid>
              </Grid>
              {/* New Sections: Education, Experience, Skills */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: brandColor, borderRadius: 1 }} />
                  Arsimi & përvoja profesionale
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Controller name="education" control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Arsimi"
                      multiline
                      rows={3}
                      fullWidth
                      size="small"
                      placeholder="p.sh. Universiteti i Prishtinës, Bachelor në Shkenca Kompjuterike, 2018-2022"
                      {...tip('Shkruaj arsimin e kandidatit (universiteti, diploma, viti).')}
                    />
                  )} />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Controller name="experience" control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Përvoja profesionale"
                      multiline
                      rows={3}
                      fullWidth
                      size="small"
                      placeholder="p.sh. Software Developer në ABC Company, 2020-2023"
                      {...tip('Shkruaj përvojën e punës së kandidatit.')}
                    />
                  )} />
              </Grid>

              <Grid item xs={12}>
                <Controller name="skills" control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Aftësitë dhe kompetencat"
                      multiline
                      rows={2}
                      fullWidth
                      size="small"
                      placeholder="p.sh. JavaScript, React, Node.js, PostgreSQL, Git"
                      {...tip('Shto aftësitë teknike dhe soft skills të kandidatit (ndaj me presje).')}
                    />
                  )} />
              </Grid>

              {/* Atributet e krahasueshme — shtesë ndaj tekstit të lirë më lart. */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: brandColor, borderRadius: 1 }} />
                  Atributet për krahasim
                </Typography>
                <Controller name="attributes" control={control}
                  render={({ field }) => (
                    <CandidateAttributeFields dense value={field.value || {}} onChange={field.onChange} />
                  )} />
              </Grid>

              {/* Pëlqimi për kontakt të ardhshëm — i ndarë nga pëlqimi i aplikimit.
                  Pa të, ky person nuk mund të ftohet për një pozitë tjetër më vonë. */}
              <Grid item xs={12}>
                <Controller name="reuseConsent" control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox size="small" checked={Boolean(field.value)} onChange={(e) => field.onChange(e.target.checked)} />}
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Kandidati pranon të kontaktohet për mundësi të ardhshme punësimi
                        </Typography>
                      }
                    />
                  )} />
              </Grid>

              {/* File Upload Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: brandColor, borderRadius: 1 }} />
                  Dokumenta (CV, Certifikata, etj.)
                </Typography>
                
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: brandColor,
                      bgcolor: 'rgba(39, 167, 105, 0.02)',
                    }
                  }}
                  onClick={() => document.getElementById('file-upload').click()}
                >
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      const maxSize = 10 * 1024 * 1024; // 10MB
                      const validFiles = [];
                      const invalidFiles = [];
                      
                      files.forEach(f => {
                        if (f.size > maxSize) {
                          invalidFiles.push(f.name);
                        } else {
                          validFiles.push({ name: f.name, size: f.size, file: f });
                        }
                      });

                      if (invalidFiles.length > 0) {
                        alert(`Këto skedarë janë shumë të mëdha (maks 10MB):\n${invalidFiles.join('\n')}`);
                      }

                      if (validFiles.length > 0) {
                        setUploadedFiles(prev => [...prev, ...validFiles]);
                      }

                      e.target.value = ''; // Reset input
                    }}
                  />
                  <UploadFileIcon sx={{ fontSize: 40, color: brandColor, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Kliko për të ngarkuar dokumente ose tërhiq këtu
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Formate të pranuara: PDF, DOC, DOCX, JPG, PNG (Maks 10MB)
                  </Typography>
                </Paper>

                {uploadedFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      {uploadedFiles.length} dokument{uploadedFiles.length > 1 ? 'e' : ''} të ngarkuar:
                    </Typography>
                    <Stack spacing={1}>
                      {uploadedFiles.map((file, idx) => (
                        <Paper 
                          key={idx} 
                          variant="outlined" 
                          sx={{ 
                            p: 1.5, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1.5,
                            borderRadius: 1.5
                          }}
                        >
                          {file.name.endsWith('.pdf') ? (
                            <PictureAsPdfIcon sx={{ color: '#d32f2f' }} />
                          ) : (
                            <DescriptionIcon sx={{ color: '#1976d2' }} />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Madhësia e panjohur'}
                            </Typography>
                          </Box>
                          <IconButton 
                            size="small" 
                            onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== idx))}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Grid>

              {/* Status & Notes Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller name="status" control={control} rules={{ required: 'Zgjidh statusin' }}
                  render={({ field }) => (
                    <TextField
                      select
                      {...field}
                      label="Statusi i aplikimit"
                      fullWidth
                      size="small"
                      error={Boolean(errors.status)}
                      helperText={errors.status?.message}
                      {...tip('Gjurmo progresin e rekrutimit për këtë kandidat.') }
                    >
                      {STATUS.map((s) => (
                        <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                      ))}
                    </TextField>
                  )} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller name="notes" control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Shënime shtesë"
                      fullWidth
                      size="small"
                      multiline
                      rows={1}
                      placeholder="Shënime të brendshme për aplikimin..."
                      {...tip('Shto shënime të brendshme që nuk do të shfaqen në PDF.')}
                    />
                  )} />
              </Grid>
              {status === 'rejected' && (
                <Grid item xs={12}>
                  <Controller name="rejectReason" control={control} rules={{ required: 'Shkruaj arsyen e refuzimit' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Arsyeja e refuzimit"
                        multiline
                        rows={3}
                        required
                        error={Boolean(errors.rejectReason)}
                        helperText={errors.rejectReason?.message}
                        {...tip('Shkruaj arsyen pse kandidati u refuzua (shfaqet në historik).')}
                      />
                    )} />
                </Grid>
              )}
              {status === 'hired' && (
                <Grid item xs={12} sm={8}>
                  <Controller name="workerId" control={control}
                    render={({ field }) => {
                      const value = workers.find(w => String(w.Id) === String(field.value)) || null;
                      return (
                        <Autocomplete
                          options={workers}
                          value={value}
                          groupBy={(w)=> w.Department || w.DepartmentName || '—'}
                          getOptionLabel={(w)=> w ? `${w.FirstName||''} ${w.LastName||''}${w.Department? ' – '+w.Department:''}` : ''}
                          filterOptions={(opts, state)=>{
                            const q = state.inputValue.toLowerCase();
                            return opts.filter(w => `${w.FirstName||''} ${w.LastName||''} ${w.Email||''} ${w.Department||w.DepartmentName||''}`.toLowerCase().includes(q));
                          }}
                          onChange={(_, val)=> field.onChange(val ? String(val.Id) : '')}
                          renderInput={(params)=> <TextField {...params} label="Lidhu me punëtorin (opsionale)" {...tip('Kërko me emër, email ose departament dhe lidhe aplikimin me punëtorin ekzistues.') } />}
                        />
                      );
                    }}
                  />
                </Grid>
              )}
              
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Professional Timeline Stepper - After Form */}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 2, md: 2.5 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(to right, rgba(39, 167, 105, 0.02), rgba(39, 167, 105, 0.05), rgba(39, 167, 105, 0.02))',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(to right, ${brandColor}, transparent, ${brandColor})`,
            opacity: 0.3
          }}
        />

        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: 700, 
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'text.primary'
          }}
        >
          <Box sx={{ width: 4, height: 20, bgcolor: brandColor, borderRadius: 1 }} />
          Procesi i rekrutimit
        </Typography>

        {/* Desktop/Tablet View - Horizontal Stepper */}
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Stepper
            activeStep={Math.max(0, STEPS.indexOf(status))}
            alternativeLabel
            sx={{
              '& .MuiStepConnector-root': {
                top: { sm: 14, md: 16 },
              },
              '& .MuiStepConnector-line': { 
                borderColor: 'divider', 
                borderTopWidth: 3,
                borderRadius: 2,
                transition: 'all 0.3s ease'
              },
              '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                borderColor: brandColor,
                borderTopWidth: 4,
                boxShadow: `0 2px 8px ${brandColor}40`
              },
              '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                borderColor: brandColor,
                borderTopWidth: 3
              },
              '& .MuiStepLabel-root': {
                cursor: 'pointer',
              },
              '& .MuiStepLabel-label': { 
                fontSize: { sm: 11, md: 12 }, 
                color: 'text.secondary',
                fontWeight: 500,
                mt: 1,
                transition: 'all 0.2s ease'
              },
              '& .MuiStepLabel-label.Mui-active': {
                fontWeight: 700,
                color: brandColor,
                fontSize: { sm: 12, md: 13 }
              },
              '& .MuiStepLabel-label.Mui-completed': {
                color: brandColor,
                fontWeight: 600
              },
              '& .MuiStepIcon-root': {
                width: { sm: 32, md: 40 },
                height: { sm: 32, md: 40 },
                transition: 'all 0.3s ease',
                border: '2px solid transparent'
              },
              '& .MuiStepIcon-root.Mui-active': { 
                color: brandColor,
                transform: 'scale(1.15)',
                filter: `drop-shadow(0 4px 12px ${brandColor}60)`,
                animation: 'pulse 2s ease-in-out infinite'
              },
              '& .MuiStepIcon-root.Mui-completed': { 
                color: brandColor,
                transform: 'scale(1.05)'
              },
              '& .MuiStepIcon-text': {
                fontWeight: 700,
                fontSize: { sm: '0.875rem', md: '1rem' }
              },
              '& .MuiStep-root': {
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  '& .MuiStepIcon-root': {
                    transform: 'scale(1.1)',
                    filter: `drop-shadow(0 2px 8px ${brandColor}40)`
                  },
                  '& .MuiStepLabel-label': {
                    color: brandColor
                  }
                }
              },
              '@keyframes pulse': {
                '0%, 100%': {
                  transform: 'scale(1.15)',
                },
                '50%': {
                  transform: 'scale(1.2)',
                }
              }
            }}
          >
            {STATUS.map((s) => (
              <Step key={s.value} onClick={() => setStatus(s.value)}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      '&.Mui-active': {
                        color: brandColor
                      },
                      '&.Mui-completed': {
                        color: brandColor
                      }
                    }
                  }}
                >
                  {s.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Mobile View - Vertical Timeline */}
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <Stack spacing={1.5}>
            {STATUS.map((s, idx) => {
              const isActive = status === s.value;
              const isCompleted = STEPS.indexOf(status) > idx;
              const stepNumber = idx + 1;

              return (
                <Paper
                  key={s.value}
                  onClick={() => setStatus(s.value)}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: isActive ? brandColor : (isCompleted ? `${brandColor}60` : 'divider'),
                    borderRadius: 2,
                    bgcolor: isActive ? `${brandColor}08` : (isCompleted ? `${brandColor}04` : 'background.paper'),
                    transition: 'all 0.3s ease',
                    transform: isActive ? 'scale(1.02)' : 'scale(1)',
                    boxShadow: isActive ? `0 4px 12px ${brandColor}30` : 'none',
                    '&:hover': {
                      borderColor: brandColor,
                      transform: 'scale(1.02)',
                      boxShadow: `0 2px 8px ${brandColor}20`
                    },
                    '&:active': {
                      transform: 'scale(0.98)'
                    }
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: isActive || isCompleted ? brandColor : 'divider',
                        color: isActive || isCompleted ? 'white' : 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        transition: 'all 0.3s ease',
                        transform: isActive ? 'scale(1.1)' : 'scale(1)',
                        boxShadow: isActive ? `0 4px 12px ${brandColor}60` : 'none'
                      }}
                    >
                      {isCompleted ? '✓' : stepNumber}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isActive ? 700 : (isCompleted ? 600 : 500),
                          color: isActive ? brandColor : (isCompleted ? brandColor : 'text.primary'),
                          fontSize: isActive ? '0.95rem' : '0.875rem',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {s.label}
                      </Typography>
                      {isActive && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            display: 'block',
                            mt: 0.25
                          }}
                        >
                          Hapi aktual
                        </Typography>
                      )}
                    </Box>
                    {isActive && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: brandColor,
                          animation: 'blink 1.5s ease-in-out infinite',
                          '@keyframes blink': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.3 }
                          }
                        }}
                      />
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Box>

        {/* Progress Indicator */}
        <Box sx={{ mt: 2.5, display: { xs: 'none', sm: 'block' } }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Progresi:
            </Typography>
            <Box sx={{ flex: 1, height: 6, bgcolor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
              <Box
                sx={{
                  height: '100%',
                  bgcolor: brandColor,
                  borderRadius: 3,
                  width: `${((STEPS.indexOf(status) + 1) / STEPS.length) * 100}%`,
                  transition: 'width 0.5s ease',
                  boxShadow: `0 0 10px ${brandColor}60`
                }}
              />
            </Box>
            <Typography variant="caption" fontWeight={700} color={brandColor}>
              {Math.round(((STEPS.indexOf(status) + 1) / STEPS.length) * 100)}%
            </Typography>
          </Stack>
        </Box>

        {/* Comment Section for Current Step */}
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ChatBubbleOutlineIcon sx={{ fontSize: '1.25rem', color: brandColor }} />
              <Typography variant="subtitle2" fontWeight={700}>
                Shto koment për: <span style={{ color: brandColor }}>{STATUS.find(x=>x.value===status)?.label || status}</span>
              </Typography>
            </Stack>
            
            <Alert severity="info" sx={{ fontSize: '0.875rem', py: 0.5 }}>
              Komenti do të ruhet në historikun e aplikimit dhe do të shfaqet në raportin PDF
            </Alert>

            <Stack direction={{ xs:'column', sm:'row' }} spacing={1.5} alignItems={{ xs:'stretch', sm:'flex-end' }}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                value={stepNote}
                onChange={(e)=> setStepNote(e.target.value)}
                placeholder={`Shkruaj koment për hapin "${STATUS.find(x=>x.value===status)?.label}"...`}
                sx={{ 
                  bgcolor: 'background.paper',
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: brandColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: brandColor,
                    }
                  }
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  const note = stepNote?.trim(); 
                  if (!note) {
                    alert('Shkruaj një koment para se ta shtosh!');
                    return;
                  }
                  const now = new Date().toISOString();
                  const sc = { ...(watch('stepComments')||{}), [status]: note };
                  reset({ ...watch(), stepComments: sc });
                  setPendingHistory((h)=> [...h, { date: now, type: 'step-comment', step: status, comment: note }]);
                  setStepNote('');
                  // Success feedback
                  const btn = document.activeElement;
                  btn.textContent = '✓ Shtuar';
                  setTimeout(() => {
                    btn.textContent = 'Shto koment';
                  }, 2000);
                }}
                startIcon={<ChatBubbleOutlineIcon />}
                sx={{ 
                  bgcolor: brandColor, 
                  '&:hover': { bgcolor: brandColor },
                  minWidth: { xs: '100%', sm: 140 },
                  whiteSpace: 'nowrap',
                  height: { xs: 'auto', sm: 40 }
                }}
              >
                Shto koment
              </Button>
            </Stack>

            {/* Show existing comment for current step */}
            {watch('stepComments')?.[status] && (
              <Paper 
                variant="outlined"
                sx={{ 
                  p: 1.5, 
                  bgcolor: `${brandColor}05`,
                  borderLeft: '3px solid',
                  borderLeftColor: brandColor,
                  borderRadius: 1.5
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <ChatBubbleOutlineIcon sx={{ fontSize: '1rem', color: brandColor, mt: 0.25 }} />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Koment ekzistues për këtë hap:
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      "{watch('stepComments')[status]}"
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Show recent history items */}
            {(prev?.history?.length > 0 || pendingHistory.length > 0) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                  Historiku i fundit ({[...(prev?.history||[]), ...pendingHistory].length} ngjarje):
                </Typography>
                <Stack spacing={1} sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {[...(prev?.history||[]), ...pendingHistory].slice(-3).reverse().map((h, idx) => (
                    <Paper 
                      key={idx}
                      variant="outlined"
                      sx={{ 
                        p: 1, 
                        borderRadius: 1,
                        borderLeft: '2px solid',
                        borderLeftColor: h.type === 'status' ? brandColor : 'divider',
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          {h.type === 'status' ? (
                            <SwapHorizIcon sx={{ fontSize: '0.875rem', color: brandColor }} />
                          ) : (
                            <ChatBubbleOutlineIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                          )}
                          <Typography variant="caption" fontWeight={600} fontSize="0.75rem">
                            {h.type === 'status' 
                              ? `${h.from || ''} → ${h.to}` 
                              : `Koment: ${h.step}`}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                          {new Date(h.date).toLocaleDateString('sq-AL', { 
                            day: '2-digit', 
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Stack>
                      {h.comment && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                          {h.comment.length > 100 ? h.comment.substring(0, 100) + '...' : h.comment}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
                {[...(prev?.history||[]), ...pendingHistory].length > 3 && (
                  <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1}>
                    ... dhe {[...(prev?.history||[]), ...pendingHistory].length - 3} ngjarje të tjera
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: { xs: 2, sm: 2.5 }, 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 4, height: 20, bgcolor: brandColor, borderRadius: 1 }} />
              Përmbledhje e aplikimit
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                    Kandidati
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    {watch('fullName') || '—'}
                  </Typography>
                  {watch('email') && (
                    <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
                      {watch('email')}
                    </Typography>
                  )}
                  {watch('phone') && (
                    <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
                      {watch('phone')}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                      Pozita
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {watch('position') || '—'}
                    </Typography>
                  </Box>
                  {watch('listName') && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                        Lista/Vacancy
                      </Typography>
                      <Typography variant="body2">
                        {watch('listName')}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                      Statusi
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip 
                        label={STATUS.find(s => s.value === status)?.label || status} 
                        color={status==='rejected' ? 'error' : status==='hired' ? 'success' : 'default'}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Box>
                </Stack>
              </Grid>
              
              {(watch('city') || watch('dateOfBirth')) && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      {watch('city') && (
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                            Qyteti
                          </Typography>
                          <Typography variant="body2">
                            {watch('city')}
                          </Typography>
                        </Box>
                      )}
                      {watch('dateOfBirth') && (
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                            Data e lindjes
                          </Typography>
                          <Typography variant="body2">
                            {new Date(watch('dateOfBirth')).toLocaleDateString('sq-AL')}
                          </Typography>
                        </Box>
                      )}
                      {watch('source') && (
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                            Burimi
                          </Typography>
                          <Typography variant="body2">
                            {sourceLabel(watch('source'))}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                </>
              )}

              {uploadedFiles.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                      Dokumenta të ngarkuar
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {uploadedFiles.slice(0, 3).map((file, idx) => (
                        <Chip 
                          key={idx}
                          icon={<AttachFileIcon />}
                          label={file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {uploadedFiles.length > 3 && (
                        <Chip 
                          label={`+${uploadedFiles.length - 3} më shumë`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0}
            sx={{ 
              border: '1px solid', 
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 2, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1 
                }}
              >
                <Box sx={{ width: 4, height: 20, bgcolor: brandColor, borderRadius: 1 }} />
                Historiku i procesit
              </Typography>
              {(prev?.history?.length || pendingHistory.length) ? (
                <Stack spacing={1.5} sx={{ maxHeight: 400, overflowY: 'auto' }}>
                  {[...(prev?.history||[]), ...pendingHistory].map((h, idx) => (
                    <Paper 
                      key={idx}
                      variant="outlined"
                      sx={{ 
                        p: 1.5, 
                        borderRadius: 1.5,
                        borderLeft: '3px solid',
                        borderLeftColor: h.type === 'status' ? brandColor : 'divider'
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            {h.type === 'status' ? (
                              <SwapHorizIcon sx={{ fontSize: '1rem', color: brandColor }} />
                            ) : (
                              <ChatBubbleOutlineIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                            )}
                            <Typography variant="caption" fontWeight={600}>
                              {h.type === 'status' 
                                ? `${h.from || ''} → ${h.to}` 
                                : (h.type==='step-comment' ? `Koment (${h.step})` : 'Koment')}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                            {new Date(h.date).toLocaleDateString('sq-AL')}
                          </Typography>
                        </Stack>
                        {h.comment && (
                          <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
                            {h.comment}
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                  S'ka ende historik për këtë aplikim
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
